import React from 'react';

// Catches any render/runtime exception in the tree below it so an unexpected
// data shape (e.g. an endpoint returning an object where the UI expects an array)
// shows a recoverable message instead of a blank white screen.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, msg: '', where: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, msg: (error && (error.message || String(error))) || 'inconnu' };
  }

  componentDidCatch(error, info) {
    // Surface to the console (and Sentry if present) for diagnosis.
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info);
    // Premier composant fautif dans la pile (ex. "at Detail" / "at Chatters") — affiché
    // à l'admin pour diagnostiquer un crash qu'on ne peut pas reproduire à distance.
    const stack = (info && info.componentStack) || '';
    const firstFrames = stack.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 4).join('  ▸ ');
    this.setState({ where: firstFrames });
    // Beacon best-effort vers le backend → apparaît dans les logs d'accès même sans endpoint dédié.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const q = `e=${encodeURIComponent((error && error.message) || 'x').slice(0, 300)}&p=${encodeURIComponent(window.location.pathname)}`;
      fetch(`${base}/api/__client_crash__?${q}`, { method: 'GET', credentials: 'omit', mode: 'no-cors' }).catch(() => {});
    } catch { /* ignore */ }
    if (window.Sentry?.captureException) {
      try { window.Sentry.captureException(error); } catch { /* ignore */ }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#08080A] flex items-center justify-center px-4 text-center">
          <div className="max-w-md">
            <div className="text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-white mb-2">Souci d'affichage</h1>
            <p className="text-sm text-slate-400 mb-2">
              Un élément de la page n'a pas pu s'afficher. <b className="text-slate-200">Recharge la page</b> et ça repart.
            </p>
            <p className="text-xs text-slate-500 mb-4">
              Ce n'est pas ta connexion, et pas besoin de redémarrer ton ordinateur. Si ça revient souvent, préviens l'admin (c'est un bug à corriger).
            </p>
            {(this.state.msg || this.state.where) && (
              <div className="text-left mb-6 rounded-lg border border-white/10 bg-black/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Détails pour l'admin (fais une capture)</div>
                <div className="text-[11px] font-mono text-rose-300 break-words">{this.state.msg}</div>
                {this.state.where && <div className="text-[10px] font-mono text-slate-400 break-words mt-1">{this.state.where}</div>}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold rounded-lg px-5 py-2.5 transition-colors"
            >
              Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
